import { DocUploadArgs, FileTags, UploadedDocument } from "@/types/Document";
import { toCamelCase, toSnakeCase } from "@/utils/Functions";
import monitor from "@/utils/monitoring";
import { ApiReponse, AuthParams, RequestObj } from "./types/api-fetch.types";
import { isAllowedMimeType, sanitizeFileName } from "@/helpers/documents-helpers";
import { attach } from "./caseApi";
import { token, keepSessionAlive } from "@/utils/session";
import { showErrorToast, showInfoToast } from "@/helpers/toast-helpers";
export interface ICreateDocument {
  authParams: AuthParams;
  file: DocUploadArgs;
}

function getAuthParams({ caseId, signature }: AuthParams): any {
  if (!signature) return null;
  return {
    sig: signature,
    caseId,
  };
}

export async function createDocument({
  authParams: { caseId, signature },
  file: { file, fileTags },
}: ICreateDocument): Promise<UploadedDocument> {
  const authParams: any = getAuthParams({ caseId, signature });
  if (!caseId) throw new Error("Case Id not Found");
  if (!isAllowedMimeType(file.type)) throw new Error("Unsupported file type");

  const sanitizedFile: File = new File([file], sanitizeFileName(file.name), { type: file.type });
  const formData = new FormData();
  formData.append("file", sanitizedFile);
  formData.append("tags", JSON.stringify(fileTags));
  const response: ApiReponse<UploadedDocument, string> = await apiFetch({
    path: `/notifier/cases/${caseId}/documents`,
    // bad api functionality still have to pass search params when url already has the case_id this is a joke
    search: authParams,
    method: "POST",
    body: formData,
    type: "multipart",
  });

  if (response.error) {
    showErrorToast(
      "Failed to upload " + file.name + " with message  " + response.error.toString().slice(0, 10)
    );
    throw new Error(response.error);
  }
  if (!response.data) throw new Error("no data" + response.error);
  if (response.data.filename && response.data.id) {
    showInfoToast("Successfully uploaded " + response.data.filename);
  }
  return response.data;
}

export async function createExpressDocument({
  file,
  fileTags,
}: DocUploadArgs): Promise<UploadedDocument> {
  if (!isAllowedMimeType(file.type)) throw new Error("Unsupported file type");
  const sanitizedFile: File = new File([file], sanitizeFileName(file.name), { type: file.type });
  const formData = new FormData();
  formData.append("file", sanitizedFile);
  formData.append("tags", JSON.stringify(fileTags));
  const response: ApiReponse<UploadedDocument, string> = await apiFetch({
    path: `/notifier/express/documents`,
    method: "POST",
    body: formData,
    type: "multipart",
  });
  if (response.error) throw new Error(response.error);
  if (!response.data) throw new Error("no data" + response.error);
  return response.data;
}
export const apiFetch = async ({
  method,
  path,
  search,
  body,
  keepCase,
  headers = {},
  timeout = 30000,
  ...restInit
}: RequestObj) => {
  try {
    const url = getUrl(path, search);
    const init = getInit(method, restInit, headers, body, keepCase);

    const controller = new AbortController();
    let timeoutId;
    if (timeout) {
      init.signal = controller.signal;
      timeoutId = setTimeout(() => controller.abort(), timeout);
    }
    const response: any = await fetch(url, init);

    if (timeoutId) clearTimeout(timeoutId);

    return response.json().then((value: any) => {
      return toCamelCase(value);
    });
  } catch (error) {
    console.log(error);
    monitor.logError({
      event: `${method} ${path}`,
      args: { search, body },
      error,
    });
    throw error;
  }
};

import { env } from "@/env";

const getUrl = (
  path: string,
  search?: { [key: string]: string | number | boolean | null | undefined }
) => {
  const url = `${env.BACKEND_HOST}/api${path}`;

  if ("settldTestModeEnabled" in window && window["settldTestModeEnabled"]) {
    if (!search) {
      search = { testModeEnabled: true };
    } else {
      search = { testModeEnabled: true, ...search };
    }
  }

  if (!search) return url;

  const params = new URLSearchParams();
  Object.entries(toSnakeCase(search)).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  });

  const sep = /[?]/.test(url) ? "&" : "?";
  return url + sep + params.toString();
};

const getInit = (method: any, restInit: any, headers: any, body?: any, keepCase?: boolean) => {
  const bearerToken = token();
  if (bearerToken) {
    keepSessionAlive(bearerToken);
    headers = { Authorization: `Bearer ${bearerToken}`, ...headers };
  }

  if (!restInit.type && !keepCase && body !== undefined) {
    body = toSnakeCase(body);
  }

  const init: RequestInit = {
    ...restInit,
    method,
    headers: { ...headers },
  };

  if (restInit.type === "multipart") {
    init.body = body;
  } else {
    init.body = JSON.stringify(body);
  }
  return init;
};

export interface UploadResult {
  success: boolean;
  document: string | null;
  filename: string;
  error?: unknown;
}
export async function uploadDocuments(
  documents: File[],
  fileTag: FileTags,
  caseId: any,
  signature: any
): Promise<UploadResult[]> {
  const uploadAndAttachToCasePromises = documents.map(async (file: File) => {
    try {
      const docToS3AndBackendRes: UploadedDocument = await createDocument({
        authParams: { caseId, signature },
        file: { file, fileTags: [fileTag] },
      });
      try {
        const linkUploadedDocToCaseRes = await attach({
          caseId,
          signature,
          documentId: docToS3AndBackendRes.id,
        });
        return {
          success: true,
          document: linkUploadedDocToCaseRes.id,
          filename: docToS3AndBackendRes.filename,
        };
      } catch (linkUploadedDocToCaseErr: any) {
        return {
          success: false,
          document: null,
          error: linkUploadedDocToCaseErr,
          filename: docToS3AndBackendRes.filename,
        };
      }
    } catch (docToS3AndBackendError: any) {
      return { success: false, document: null, error: docToS3AndBackendError, filename: file.name };
    }
  });
  return Promise.all(uploadAndAttachToCasePromises);
}
